import { getAssetKey } from '@/lib/assets/assets'
import { Dispatch, useCallback, useMemo, useState } from 'react'
import { AssetSummary, GetAssetsResponse } from '@/lib/assets/types'
import { InfiniteData } from '@tanstack/query-core'
import { UseInfiniteQueryResult } from '@tanstack/react-query'

export type UseAssetsPopupNavigationControlsProps = {
  data?: InfiniteData<GetAssetsResponse>
  selectedAsset: AssetSummary | null
  setSelectedAsset: Dispatch<AssetSummary | null>
  fetchNextPage?: () => Promise<UseInfiniteQueryResult<InfiniteData<GetAssetsResponse>>>
}

export default function useAssetsPopupNavigationControls({
  data,
  selectedAsset,
  setSelectedAsset,
  fetchNextPage,
}: UseAssetsPopupNavigationControlsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const selectedAssetKey = useMemo(() => {
    return selectedAsset ? getAssetKey(selectedAsset) : ''
  }, [selectedAsset])

  const getAssetPageIndex = useCallback(() => {
    return (
      data?.pages.findIndex((page) => {
        return page.data.some((asset) => getAssetKey(asset) === selectedAssetKey)
      }) ?? -1
    )
  }, [data?.pages, selectedAssetKey])

  const getAssetIndexInPage = useCallback(() => {
    const pageIndex = getAssetPageIndex()

    if (pageIndex === -1) {
      return -1
    }

    return data?.pages[pageIndex].data.findIndex((asset) => getAssetKey(asset) === selectedAssetKey) ?? -1
  }, [data?.pages, getAssetPageIndex, selectedAssetKey])

  const hasPreviousAsset = useMemo(() => {
    // There are items
    if (!data?.pages.length) {
      return false
    }

    if (data.pages.length && !data.pages[0].meta?.total_items) {
      return false
    }

    // Asset is not first asset in first page
    return !(getAssetPageIndex() === 0 && getAssetIndexInPage() <= 0)
  }, [data?.pages, getAssetIndexInPage, getAssetPageIndex])

  const hasNextAsset = useMemo(() => {
    // There are items
    if (!data?.pages.length) {
      return false
    }

    if (data.pages.length && !data.pages[0].meta?.total_items) {
      return false
    }

    // Asset is not the last asset in last page
    const lastPageIndex = data.pages.length - 1
    const lastPage = data.pages[lastPageIndex]
    const lastPageAssetCount = lastPage.data.length
    const lastPageAssetIndex = getAssetIndexInPage()
    const lastPageAssetKey = getAssetKey(lastPage.data[lastPageAssetCount - 1])
    const isLastPage = getAssetPageIndex() === lastPageIndex
    const isLastAsset = lastPageAssetKey === selectedAssetKey
    const isLastAssetInPage = lastPageAssetIndex === lastPageAssetCount - 1
    return !(isLastPage && isLastAsset && isLastAssetInPage)
  }, [data?.pages, getAssetIndexInPage, getAssetPageIndex, selectedAssetKey])

  const navigateToPreviousAsset = useCallback(() => {
    if (!data) {
      return
    }

    if (!selectedAsset || !hasPreviousAsset) {
      return
    }

    const currentPageIndex = getAssetPageIndex()
    const currentAssetIndex = getAssetIndexInPage()

    // If not the first asset in the page, simply get the previous one
    if (currentAssetIndex > 0) {
      setSelectedAsset(data.pages[currentPageIndex].data[currentAssetIndex - 1])
      return
    }

    // If it's the first asset in the page, get the last asset from the previous page
    const previousPageIndex = currentPageIndex - 1
    const previousPage = data.pages[previousPageIndex]
    const lastAssetInPreviousPage = previousPage.data[previousPage.data.length - 1]
    setSelectedAsset(lastAssetInPreviousPage)
  }, [data, getAssetIndexInPage, getAssetPageIndex, hasPreviousAsset, selectedAsset, setSelectedAsset])

  const navigateToNextAsset = useCallback(() => {
    if (!data) {
      return
    }

    if (!selectedAsset || !hasNextAsset) {
      return
    }

    const currentPageIndex = getAssetPageIndex()
    const currentAssetIndex = getAssetIndexInPage()
    const currentPage = data.pages[currentPageIndex]

    // If not the last asset in the page, get the next one
    if (currentAssetIndex < currentPage.data.length - 1) {
      setSelectedAsset(data.pages[currentPageIndex].data[currentAssetIndex + 1])
      return
    }

    // If it's the last asset in the page but not the last page, get the first asset from the next page
    const nextPageIndex = currentPageIndex + 1

    // Check if the next page is already loaded
    if (nextPageIndex < data.pages.length) {
      const nextPage = data.pages[nextPageIndex]
      const firstAssetInNextPage = nextPage.data[0]
      setSelectedAsset(firstAssetInNextPage)
      return
    }

    // If the next page is not loaded yet, fetch it first
    if (fetchNextPage) {
      setIsLoading(true)
      fetchNextPage()
        .then((result) => {
          const firstAssetInNextPage = result?.data?.pages[nextPageIndex]?.data[0]
          if (firstAssetInNextPage) {
            setSelectedAsset(firstAssetInNextPage)
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [data, getAssetIndexInPage, getAssetPageIndex, hasNextAsset, selectedAsset, setSelectedAsset, fetchNextPage])

  return {
    isLoading,
    hasPreviousAsset,
    hasNextAsset,
    navigateToPreviousAsset,
    navigateToNextAsset,
  }
}
